import { useState, type KeyboardEvent } from 'react';
import { Platform, Pressable, type PressableProps } from 'react-native';

import { Box, Text, useTheme } from 'app/shopify/theme';

export type SwitchSize = 'default' | 'small';
export type SwitchState = 'enabled' | 'disabled';

export interface SwitchProps extends Pick<PressableProps, 'accessibilityLabel' | 'accessibilityHint' | 'testID'> {
  /** Also names the switch for assistive technology when the label is hidden. */
  label?: string;
  showLabel?: boolean;
  /** Controlled value; update it in onValueChange or the existing onPress callback. */
  selected?: boolean;
  size?: SwitchSize;
  state?: SwitchState;
  disabled?: boolean;
  inverted?: boolean;
  fullWidth?: boolean;
  onValueChange?: (selected: boolean) => void;
  onPress?: () => void;
}

function getSwitchColors(selected: boolean, disabled: boolean, inverted: boolean) {
  const disabledColor = inverted ? 'surface.inverse.disabled' : 'surface.disabled';
  return {
    track: disabled
      ? disabledColor
      : selected
        ? inverted
          ? 'surface.inverse.selected'
          : 'surface.selected-strong'
        : 'surface.tertiary-neutral',
    knob: disabled ? disabledColor : inverted ? 'surface.inverse.default' : 'surface.default',
    label: disabled ? 'text.disabled' : selected ? 'text.brand' : 'text.secondary',
    border: disabled && inverted ? 'border.inverse.divider' : 'border.secondary',
  } as const;
}

function getStateLayer(pressed: boolean, hovered: boolean, disabled: boolean, inverted: boolean) {
  if (disabled) return undefined;
  if (pressed) return inverted ? 'state-layer.inverse.press-brand' : 'state-layer.press-brand';
  if (hovered) return inverted ? 'state-layer.inverse.hover-brand' : 'state-layer.hover-brand';
  return undefined;
}

export function Switch({
  label = 'Label',
  showLabel = false,
  selected = true,
  size = 'default',
  state = 'enabled',
  disabled = false,
  inverted = false,
  fullWidth = false,
  onValueChange,
  onPress,
  accessibilityLabel,
  accessibilityHint,
  testID,
}: SwitchProps) {
  const theme = useTheme();
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [spacePressed, setSpacePressed] = useState(false);
  const isDisabled = disabled || state === 'disabled';
  const isSmall = size === 'small';
  const sizing = theme.tokens.sizing;
  const trackWidth = Number(sizing[isSmall ? 'sizing.general.x12' : 'sizing.general.x14']);
  const trackHeight = Number(sizing[isSmall ? 'sizing.general.x6' : 'sizing.general.x8']);
  const knobSize = Number(sizing[isSmall ? 'sizing.general.x5' : 'sizing.general.x7']);
  const knobInset = theme.spacing['spacing.general.x0,5'];
  const focusOffset = theme.spacing['spacing.general.x1'];
  const colors = getSwitchColors(selected, isDisabled, inverted);

  function handlePress() {
    if (isDisabled) return;
    onValueChange?.(!selected);
    onPress?.();
  }

  // React Native Web's Pressable handles Enter, but only handles Space for button roles.
  const webKeyboardProps =
    Platform.OS === 'web'
      ? {
          onKeyDownCapture: (event: KeyboardEvent<HTMLElement>) => {
            if (event.key !== ' ' || isDisabled) return;
            event.preventDefault();
            setSpacePressed(true);
          },
          onKeyUpCapture: (event: KeyboardEvent<HTMLElement>) => {
            if (event.key !== ' ') return;
            event.preventDefault();
            setSpacePressed(false);
            if (spacePressed) handlePress();
          },
        }
      : {};

  return (
    <Pressable
      {...webKeyboardProps}
      accessibilityRole="switch"
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityHint={accessibilityHint}
      accessibilityState={{ checked: selected, disabled: isDisabled }}
      aria-checked={selected}
      disabled={isDisabled}
      focusable={!isDisabled}
      testID={testID}
      hitSlop={theme.spacing['spacing.general.x1'] + theme.spacing['spacing.general.x0,5']}
      onPress={handlePress}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        setSpacePressed(false);
      }}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={{
        alignSelf: fullWidth ? 'stretch' : 'flex-start',
        maxWidth: '100%',
        outlineStyle: 'solid',
        outlineWidth: 0,
      }}
    >
      {({ pressed }) => {
        const stateLayer = getStateLayer(pressed || spacePressed, hovered, isDisabled, inverted);
        return (
          <Box
            flexDirection="row"
            alignItems="center"
            justifyContent={fullWidth ? 'space-between' : 'flex-start'}
            gap="spacing.general.x2"
            minHeight={Number(sizing['sizing.general.x8'])}
          >
            {showLabel ? (
              <Text variant={isSmall ? 'bodySmall' : 'bodyBase'} color={colors.label} flexShrink={1}>
                {label}
              </Text>
            ) : null}

            <Box width={trackWidth} height={trackHeight} flexShrink={0}>
              <Box
                width={trackWidth}
                height={trackHeight}
                borderRadius="radius.full"
                backgroundColor={colors.track}
                overflow="hidden"
              >
                {/* A separate border keeps the knob's inset identical in every state. */}
                {isDisabled || !selected ? (
                  <Box
                    position="absolute"
                    top={0}
                    bottom={0}
                    left={0}
                    right={0}
                    borderRadius="radius.full"
                    borderWidth={theme.borderWidths['border-width.subtle']}
                    borderColor={colors.border}
                  />
                ) : null}
                {stateLayer ? (
                  <Box position="absolute" top={0} bottom={0} left={0} right={0} backgroundColor={stateLayer} />
                ) : null}
                <Box
                  position="absolute"
                  top={knobInset}
                  left={selected ? undefined : knobInset}
                  right={selected ? knobInset : undefined}
                  width={knobSize}
                  height={knobSize}
                  borderRadius="radius.full"
                  backgroundColor={colors.knob}
                />
              </Box>
              {focused && !isDisabled ? (
                <Box
                  pointerEvents="none"
                  position="absolute"
                  top={-focusOffset}
                  bottom={-focusOffset}
                  left={-focusOffset}
                  right={-focusOffset}
                  borderRadius="radius.full"
                  borderWidth={theme.borderWidths['border-width.emphasized']}
                  borderColor="border.focused"
                />
              ) : null}
            </Box>
          </Box>
        );
      }}
    </Pressable>
  );
}
